import { useParams } from "react-router-dom";
import GameTable from "@/components/GameTable";
import { FixtureInfo, RowValues, Season, PlayerInfo, PlayerStats } from "@/types";
import { useEffect, useState } from "react";
import { AverageCalculatedColumn, getRowValuesForFixturePlayerStats, AverageCalculatedColumnNOTP90, fillNullDataForPlayerStats, FINISHED_STATES_STRING } from "@/utils/stats.tsx";
import { PLAYER_POSITIONS } from "@/utils/playerPositions";
import PlayerLogo from "@/components/player-logo";
import PremiumGuard from "@/components/premium-guard";
import { fetchDataAfterRefresh, useUserStore, fetchDataWithArgsAfterRefresh } from "@/components/login/loginStore";
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import { isMobile } from "@/stores/MenuStore";

export function getPlayerPositionName(positionId: number) {
    const position = PLAYER_POSITIONS.find(pos => pos.id === positionId);
    if (position) {
        switch (position.code) {
            case 'goalkeeper':
                return 'player.player_1';
            case 'defender':
                return 'player.player_2';
            case 'midfielder':
                return 'player.player_3';
            case 'attacker':
                return 'player.player_4';
            case 'centre-back':
                return 'player.player_5';
            case 'defensive-midfied':
                return 'player.player_6';
            case 'attacking-midfied':
                return 'player.player_7';
            case 'centre-forward':
                return 'player.player_8';
            case 'left-wing':
                return 'player.player_9';
            case 'central-midfied':
                return 'player.player_10';
            case 'right-back':
                return 'player.player_11';
            case 'left-back':
                return 'player.player_12';
            case 'right-wing':
                return 'player.player_13';
            case 'left-midfield':
                return 'player.player_14';
            case 'right-midfield':
                return 'player.player_15';
            case 'secondary_striker':
                return 'player.player_16';
            case 'coach':
                return 'player.player_17';
            case 'assistant-coach':
                return 'player.player_18';
            case 'goalkeeping-coach':
                return 'player.player_19';
            case 'forward-coach':
                return 'player.player_20';
            case 'caretaker-manager':
                return 'player.player_21';
            default:
                return 'player.player_22';
        }
    }
    return 'player.player_22';
}

export default function Player() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const user = useUserStore((state) => state);

    const [games, setGames] = useState<FixtureInfo[]>([]);
    const [rowValues, setRowValues] = useState<RowValues[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [playerInfo, setPlayerInfo] = useState<PlayerInfo | null>(null);
    const [seasons, setSeasons] = useState<Season[]>([]);
    const [leaguesNames, setLeaguesNames] = useState<string[]>([]);
    const [home, setHome] = useState<boolean>(true);
    const [away, setAway] = useState<boolean>(true);
    const [playerGamesFiltered, setPlayerGamesFiltered] = useState<FixtureInfo[]>([]);

    const fetchStatsData = (windowSize: string, season: string | null, leagues?: string[], homeOrAway?: string | null) => {
        setLoading(true);
        if (windowSize && windowSize != '') {
            fetchDataWithArgsAfterRefresh(['/api/fixture_info_by_player_id', '/not_premium_endpoints/player_info.json'],
                {
                    player_id: id, window: windowSize, state: FINISHED_STATES_STRING,
                    ...season && { seasons_ids: season },
                    leagues: leagues,
                    ...homeOrAway && { home_or_away: homeOrAway }
                },
                (data) => { setGames(data) }
            );
        }
    };

    const playedAtHome: { [key: number]: number } = {};
    games.forEach((game) => {
        playedAtHome[game.id] = game.home_id;
    });

    useEffect(() => {
        if (games.length && playerInfo && id) {
            setLoading(true);
            fetchDataAfterRefresh(
                ['/api/player_stats_by_fixture_ids?player_id=' + id + '&fixture_ids=' +
                    games.map((game: FixtureInfo) => game.id).join(','), '/not_premium_endpoints/player_stats.json'],
                (data) => {
                    let filteredFixtureIds = data.map((game: PlayerStats) => game.fixture_id);
                    let gamesFiltered = games.filter((game) => filteredFixtureIds.includes(game.id));
                    setPlayerGamesFiltered(gamesFiltered);
                    const rv = getRowValuesForFixturePlayerStats(fillNullDataForPlayerStats(data),
                        gamesFiltered,
                        id, user.premium);
                    setRowValues(rv);
                    setLoading(false);
                }
            );
        } else {
            setRowValues([]);
            setLoading(false);
        }
    }, [games]);

    useEffect(() => {
        fetchDataAfterRefresh(['/api/player_by_id?player_id=' + id, '/not_premium_endpoints/pl_info.json'], (data) => {
            setPlayerInfo(data);
        });
        fetchDataAfterRefresh([`/api/season_by_player_id?player_id=${id}&state=${FINISHED_STATES_STRING}&has_stats=true`, "/not_premium_endpoints/season_by_team_id.json"], (data: Season[]) => {
            const seasons = data;
            const sortedSeasons = seasons.sort((a, b) => {
                const [startYearA] = a.name.split('/').map(Number);
                const [startYearB] = b.name.split('/').map(Number);
                return startYearB - startYearA;
            });
            setSeasons(sortedSeasons);
            const seasonsIds = [...new Set(sortedSeasons.map(season => season.id))];
            fetchDataWithArgsAfterRefresh('/api/league_by_seasons_ids', { seasons_ids: seasonsIds }, (data) => {
                setLeaguesNames(data.map((l: { id: number; name: string }) => ({ value: l.id, label: l.name })));
            }, false);
        }, user.premium);
    }, [id]);

    return (
        <PremiumGuard>
            <div className="fadeIn">
                <SEO
                    title={`${playerInfo?.name || t('player.player_23')} | Craque Stats`}
                    description={t('player.player_24', { name: playerInfo?.name })}
                />
                <h1 className={`text-3xl ${isMobile() && 'mb-2'}`}>
                    {playerInfo && <PlayerLogo playerId={playerInfo.id} className='inline-block mr-2 rounded' />}
                    {playerInfo && playerInfo.name}
                    {playerInfo && <span className="text-sm text-default-400"> {t(getPlayerPositionName(playerInfo.position_id))}</span>}
                </h1>
                {!isMobile() && <br />}
                {
                    <GameTable loading={loading}
                        onChange={(windowSize: string, season: string | null, league?: string[], homeOrAway?: string | null) => fetchStatsData(windowSize, season, league, homeOrAway)}
                        seasons={seasons}
                        targetPlayer={playerInfo}
                        leaguesNames={leaguesNames}
                        fillZeroOnColumn={true}
                        label={t('player.player_25')}
                        calculatedColumns={[
                            AverageCalculatedColumnNOTP90, AverageCalculatedColumn
                        ]} games={playerGamesFiltered} rowValues={rowValues}
                        typeTable='player'
                        home={home} setHome={setHome}
                        away={away} setAway={setAway}
                        statsList={rowValues}
                    />
                }
            </div>
        </PremiumGuard>
    );
}